import {
  Action,
  AnyAction,
  combineReducers,
  configureStore,
  ThunkAction,
} from '@reduxjs/toolkit';

import {
  accountBalanceFetchReducer,
  accountsFetchReducer,
  allAccountBalanceFetchReducer,
  transactionHistoryReducer,
} from './slices/account';
import {
  limitByBusinessReducer,
  limitListReducer,
  updateTransactionLimitReducer,
} from './slices/accountLimits';
import {
  businessDirectorCreationReducer,
  cddResponsesReducer,
  getDirectorAdditionalInfoReducer,
  getDirectorsByBusinessIdReducer,
  submitDirectorAdditionalInfoReducer,
} from './slices/addDirector';
import {
  getDocumentRequestReducer,
  getDocumentRequestV2Reducer,
  uploadAdditionalDocumentsReducer,
} from './slices/additionalDocument';
import {
  addBusinessToAggregatorReducer,
  getAggregateMerchantsTransactionsReducer,
  getAggregateMerchantTransactionsReducer,
  getAggregatorByCodeReducer,
  getAggregatorMerchantsReducer,
  getAggregatorQRCodeReducer,
  getAggregatorStatsReducer,
  inviteBusinessToAggregatorNetworkReducer,
} from './slices/aggregator';
import {
  getBusinessByIdReducer,
  resetSecretKeyReducer,
  updateWebhookReducer,
} from './slices/apiKeys';
import appToastReducer from './slices/appToast/appToastSlice';
import {
  accessTokenExpirationReducer,
  authReducer,
  checkIfUserEmailExistsReducer,
  checkIfUserPhoneExistsReducer,
  completeMfaReducer,
  devAuthReducer,
  oAuthLoginReducer,
  onboardExistingBusinessOwnerReducer,
  refreshTokenReducer,
  registerRetailUserReducer,
  resendLoginOtpReducer,
  userRegistrationReducer,
} from './slices/auth';
import {
  deleteBeneficiaryReducer,
  saveBeneficiaryReducer,
} from './slices/beneficiaries';
import {
  clientIdValidationReducer,
  getDepositsTransactionsReducer,
  processPaymentReducer,
} from './slices/bet9jaDeposits';
import { getWalletTransactionsReducer } from './slices/betting';
import {
  deleteBillBeneficiaryReducer,
  saveBillBeneficiaryReducer,
  updateBillBeneficiaryReducer,
} from './slices/billPaymentBeneficiaries';
import {
  billersCategoriesReducer,
  billsPaymentReducer,
  billTransactionsReducer,
  customerLookupReducer,
} from './slices/billsPayment';
import {
  allowedBusinessTypesFetchReducer,
  businessAdditionReducer,
  businessCategoriesFetchReducer,
  businessCreationReducer,
  businessDeletionReducer,
  businessDocumentCreationReducer,
  businessRepresentativeCreationReducer,
  saveBusinessReducer,
  userBusinessesFetchReducer,
  userDetailsForBusinessFetchReducer,
} from './slices/business';
import { bvnVerificationCreationReducer } from './slices/bvnVerification';
import {
  circuitMakePaymentReducer,
  circuitProductsFetchReducer,
  circuitUserHistoryReducer,
} from './slices/circuit';
import {
  getAggregatorReferralsCommissionsReducer,
  getCommissionBalanceReducer,
  getCommissionOverviewReducer,
  getEarnedCommissionsReducer,
  getReferralsCommissionsReducer,
  getSecondaryReferralsCommissionsReducer,
  getWithdrawnCommissionsReducer,
  withdrawCommissionReducer,
} from './slices/commissions';
import { environmentReducer } from './slices/devPlatformEnvironment';
import {
  checkSlugExistReducer,
  optInReducer,
} from './slices/devPlatformGetStarted';
import {
  emailVerificationCreationReducer,
  emailVerificationVerifyReducer,
} from './slices/emailVerification';
import {
  bookFlightReducer,
  getAirportsReducer,
  getFlightTransactionsReducer,
  getSingleFlightReducer,
  searchFlightsReducer,
} from './slices/flights';
import {
  getActiveGiftCardProviderByIdReducer,
  getActiveGiftCardProvidersReducer,
  getAllCountriesReducer,
  getAllGiftCardsReducer,
  // getAllGiftCardsReducer,
  getCountryByIdReducer,
  getCustomersTransactionsReducer,
  getGiftCardPricesReducer,
  // getGiftCardPricesReducer,
  makeGiftCardPaymentsReducer,
} from './slices/giftCards';
import {
  approveRepresentativeReducer,
  createIdVerificationReducer,
  getCDDQuestionsReducer,
  getKybDetailsReducer,
  getRepresentativeDetailsReducer,
  kybGetByIdReducer,
  ninVerificationReducer,
  representativeAddressReducer,
  submitCDDQuestionsReducer,
  submitRejectedResidentPermitReducer,
  submitResidentPermitReducer,
  submitTinReducer,
} from './slices/kyb';
import { kycGetByIdReducer } from './slices/kyc';
import { citiesFetchReducer, statesFetchReducer } from './slices/location';
import {
  createOutletReducer,
  deleteOutletReducer,
  disableOutletReducer,
  enableOutletReducer,
  getOutletsReducer,
  updateOutletReducer,
} from './slices/outlet';
import { permissionsFetchReducer } from './slices/permissions';
import {
  phoneVerificationCreationReducer,
  phoneVerificationVerifyReducer,
} from './slices/phoneNumberVerification';
import { setPinReducer } from './slices/pin';
import {
  generateReferralCodeReducer,
  getReferralCommissionStatsReducer,
  getReferralHistoryReducer,
  getReferralStatsReducer,
  inviteRefereeByMailReducer,
  promptRefereeReducer,
} from './slices/referrals';
import {
  requestResetPasscodeReducer,
  resetOfflinePasscodeReducer,
  resetPasscodeReducer,
} from './slices/resetPasscode';
import {
  roleCreationReducer,
  roleDeleteReducer,
  rolesFetchReducer,
  roleUpdateReducer,
} from './slices/roles';
import {
  createAccountLogoReducer,
  getAccountLogosReducer,
} from './slices/savings/accountLogos';
import {
  createAutoDebitConfigReducer,
  deleteAutoDebitConfigReducer,
  editAutoDebitConfigReducer,
  searchAutoDebitConfigsReducer,
} from './slices/savings/autoDebit';
import {
  createSavingsBucketReducer,
  deleteBucketReducer,
  getBucketsByAccountNumberReducer,
  getSavingsBucketReducer,
  rolloverBucketReducer,
  updateBucketNameReducer,
} from './slices/savings/buckets';
import {
  bucketTopUpDepositReducer,
  searchDepositsReducer,
} from './slices/savings/deposits';
import {
  dailyAccruedInterestReducer,
  totalAccruedInterestByUserReducer,
  totalAccruedInterestReducer,
} from './slices/savings/interest';
import { searchSavingsProductsReducer } from './slices/savings/products';
import { savingsRatesReducer } from './slices/savings/rates';
import {
  createSpendAndStashReducer,
  deleteSpendAndStashReducer,
  editSpendAndStashReducer,
  searchSpendAndStashReducer,
} from './slices/savings/spendAndStash';
import { getBucketTransactionsReducer } from './slices/savings/transactions';
import {
  makeWithdrawalReducer,
  searchWithdrawalsReducer,
} from './slices/savings/withdrawals';
import {
  changeMfaReducer,
  changePinReducer,
  completeMfaSetupReducer,
  disableMfaReducer,
  enableMfaReducer,
  getUserSettingsReducer,
  requestChangeMfaReducer,
  requestDisableMfaReducer,
  resendMfaOtpReducer,
  resetPinReducer,
  setupMfaReducer,
} from './slices/security';
import {
  acceptTeamMemberReducer,
  activateTeamMemberReducer,
  deactivateTeamMemberReducer,
  deleteTeamMemberReducer,
  getTeamMemberInviteDetailsReducer,
  getTeamMembersReducer,
  inviteTeamMemberReducer,
  updateRoleReducer,
  updateTeamMemberProfileReducer,
  updateTeamMemberReducer,
} from './slices/teamMembers';
import {
  checkTransaction2faReducer,
  verifyTransaction2faTokenReducer,
} from './slices/transaction2fa';
import {
  approveTransactionReducer,
  bulkApproveTransactionReducer,
  bulkDeclineTransactionReducer,
  createTransactionMandateReducer,
  declineTransactionReducer,
  deleteTransactionMandateReducer,
  disableTransactionMandateReducer,
  enableTransactionMandateReducer,
  generateAccountStatementReducer,
  getPendingTransactionsForApprovalReducer,
  getPendingTransactionsForInitiatorReducer,
  getTransactionCategoriesReducer,
  getTransactionMandatesReducer,
  updateTransactionMandateReducer,
} from './slices/transactionMandate';
import {
  createOutletTransactionMandateReducer,
  getOutletTransactionMandateReducer,
  getPendingTransactionsReducer,
  updateOutletTransactionMandateReducer,
} from './slices/transactionMandateV2';
import {
  getTransactionThresholdsReducer,
  setTransactionThresholdReducer,
  toggleGlobalThresholdReducer,
  toggleTransactionThresholdReducer,
} from './slices/transactionThresholds';
import {
  cancelScheduledTransferReducer,
  getBanksReducer,
  getBulkTransfersReducer,
  getScheduledTransfersReducer,
  getSingleTransfersReducer,
  initiateBulkTransferReducer,
  initiateMultipleTransferReducer,
  initiateScheduledTransferReducer,
  initiateTransferReducer,
  nameEnquiryReducer,
  pauseScheduledTransferReducer,
  resumeScheduledTransferReducer,
  updateScheduledTransferReducer,
} from './slices/transfer';
import { userProfileReducer } from './slices/user';
import {
  createPermanentVAccountReducer,
  createTransientVAccountReducer,
  getVirtualAccountBalanceReducer,
  getVirtualAccountDetailsReducer,
  getVirtualAccountsReducer,
  getVirtualAccountTransactionsReducer,
} from './slices/virtualAccounts';

const reducers = {
  appToast: appToastReducer,
  auth: authReducer,
  completeMfa: completeMfaReducer,
  resendLoginOtp: resendLoginOtpReducer,
  devAuth: devAuthReducer,
  accountBalance: accountBalanceFetchReducer,
  allAccountBalances: allAccountBalanceFetchReducer,
  accessTokenExpiration: accessTokenExpirationReducer,
  refreshToken: refreshTokenReducer,
  accounts: accountsFetchReducer,
  businessCategoriesFetch: businessCategoriesFetchReducer,
  businessCreation: businessCreationReducer,
  businessAddition: businessAdditionReducer,
  businessDocumentCreation: businessDocumentCreationReducer,
  businessRepresentativeCreation: businessRepresentativeCreationReducer,
  bvnVerificationCreation: bvnVerificationCreationReducer,
  citiesFetch: citiesFetchReducer,
  emailVerificationCreation: emailVerificationCreationReducer,
  emailVerificationVerify: emailVerificationVerifyReducer,
  kybGetById: kybGetByIdReducer,
  kycGetById: kycGetByIdReducer,
  permissionsFetch: permissionsFetchReducer,
  phoneVerificationCreation: phoneVerificationCreationReducer,
  phoneVerificationVerify: phoneVerificationVerifyReducer,
  requestResetPasscode: requestResetPasscodeReducer,
  resetPasscode: resetPasscodeReducer,
  resetOfflinePasscode: resetOfflinePasscodeReducer,
  roleCreation: roleCreationReducer,
  roleDelete: roleDeleteReducer,
  rolesFetch: rolesFetchReducer,
  roleUpdate: roleUpdateReducer,
  saveBusiness: saveBusinessReducer,
  statesFetch: statesFetchReducer,
  transactionHistory: transactionHistoryReducer,
  userBusinesses: userBusinessesFetchReducer,
  userRegistration: userRegistrationReducer,
  billerCategories: billersCategoriesReducer,
  billsPayment: billsPaymentReducer,
  billTransactions: billTransactionsReducer,
  customerLookup: customerLookupReducer,
  banksFetch: getBanksReducer,
  nameEnquiry: nameEnquiryReducer,
  setPin: setPinReducer,
  initiateTransfer: initiateTransferReducer,
  createOutlet: createOutletReducer,
  deleteOutlet: deleteOutletReducer,
  disableOutlet: disableOutletReducer,
  enableOutlet: enableOutletReducer,
  getOutlets: getOutletsReducer,
  updateOutlet: updateOutletReducer,
  userDetailsForBusinessFetch: userDetailsForBusinessFetchReducer,
  getTeamMembers: getTeamMembersReducer,
  inviteTeamMember: inviteTeamMemberReducer,
  activateTeamMember: activateTeamMemberReducer,
  deactivateTeamMember: deactivateTeamMemberReducer,
  deleteTeamMember: deleteTeamMemberReducer,
  updateTeamMember: updateTeamMemberReducer,
  updateTeamMemberProfile: updateTeamMemberProfileReducer,
  getTeamMemberInviteDetails: getTeamMemberInviteDetailsReducer,
  acceptTeamMember: acceptTeamMemberReducer,
  getRepresentativeDetails: getRepresentativeDetailsReducer,
  approveRepresentative: approveRepresentativeReducer,
  getTransactionCategories: getTransactionCategoriesReducer,
  createTransactionMandate: createTransactionMandateReducer,
  getTransactionMandates: getTransactionMandatesReducer,
  enableTransactionMandate: enableTransactionMandateReducer,
  disableTransactionMandate: disableTransactionMandateReducer,
  deleteTransactionMandate: deleteTransactionMandateReducer,
  getPendingTransactionsForApproval: getPendingTransactionsForApprovalReducer,
  approveTransaction: approveTransactionReducer,
  declineTransaction: declineTransactionReducer,
  oAuthLogin: oAuthLoginReducer,
  registerRetailUser: registerRetailUserReducer,
  updateTransactionMandate: updateTransactionMandateReducer,
  createIdVerification: createIdVerificationReducer,
  clientIdValidation: clientIdValidationReducer,
  processPayment: processPaymentReducer,
  getDepositsTransactions: getDepositsTransactionsReducer,
  initiateMultipleTransfer: initiateMultipleTransferReducer,
  initiateBulkTransfer: initiateBulkTransferReducer,
  initiateScheduledTransfer: initiateScheduledTransferReducer,
  getCommissionBalance: getCommissionBalanceReducer,
  getAggregatorByCode: getAggregatorByCodeReducer,
  addBusinessToAggregator: addBusinessToAggregatorReducer,
  inviteBusinessToAggregatorNetwork: inviteBusinessToAggregatorNetworkReducer,
  getAggregateMerchantsTransactions: getAggregateMerchantsTransactionsReducer,
  getAggregateMerchantTransactions: getAggregateMerchantTransactionsReducer,
  getWithdrawnCommissions: getWithdrawnCommissionsReducer,
  getEarnedCommissions: getEarnedCommissionsReducer,
  limitList: limitListReducer,
  limitByBusiness: limitByBusinessReducer,
  updateTransactionLimit: updateTransactionLimitReducer,
  withdrawCommission: withdrawCommissionReducer,
  getAggregatorStats: getAggregatorStatsReducer,
  getAggregatorMerchants: getAggregatorMerchantsReducer,
  getCommissionOverview: getCommissionOverviewReducer,
  generateReferralCode: generateReferralCodeReducer,
  getReferralStats: getReferralStatsReducer,
  getReferralHistory: getReferralHistoryReducer,
  promptReferee: promptRefereeReducer,
  getDocumentRequest: getDocumentRequestReducer,
  getDocumentRequestV2: getDocumentRequestV2Reducer,
  inviteRefereeByMail: inviteRefereeByMailReducer,
  uploadAdditionalDocuments: uploadAdditionalDocumentsReducer,
  getKybDetails: getKybDetailsReducer,
  submitTin: submitTinReducer,
  getCDDQuestions: getCDDQuestionsReducer,
  submitCDDQuestions: submitCDDQuestionsReducer,
  getReferralsCommissions: getReferralsCommissionsReducer,
  getReferralCommissionStats: getReferralCommissionStatsReducer,
  changePin: changePinReducer,
  resetPin: resetPinReducer,
  userSettings: getUserSettingsReducer,
  setupMfa: setupMfaReducer,
  completeMfaSetup: completeMfaSetupReducer,
  enableMfa: enableMfaReducer,
  requestDisableMfa: requestDisableMfaReducer,
  disableMfa: disableMfaReducer,
  requestChangeMfa: requestChangeMfaReducer,
  changeMfa: changeMfaReducer,
  resendMfaOtp: resendMfaOtpReducer,
  checkTransaction2fa: checkTransaction2faReducer,
  verifyTransaction2faToken: verifyTransaction2faTokenReducer,
  getTransactionThresholds: getTransactionThresholdsReducer,
  setTransactionThreshold: setTransactionThresholdReducer,
  toggleTransactionThreshold: toggleTransactionThresholdReducer,
  toggleGlobalThreshold: toggleGlobalThresholdReducer,
  checkIfUserEmailExists: checkIfUserEmailExistsReducer,
  checkIfUserPhoneExists: checkIfUserPhoneExistsReducer,
  onboardExistingBusinessOwner: onboardExistingBusinessOwnerReducer,
  getPendingTransactionsForInitiator: getPendingTransactionsForInitiatorReducer,
  generateAccountStatement: generateAccountStatementReducer,
  userProfile: userProfileReducer,
  getSecondaryReferralsCommissions: getSecondaryReferralsCommissionsReducer,
  getAggregatorReferralsCommissions: getAggregatorReferralsCommissionsReducer,
  getBulkTransfers: getBulkTransfersReducer,
  getScheduledTransfers: getScheduledTransfersReducer,
  pauseScheduledTransfer: pauseScheduledTransferReducer,
  resumeScheduledTransfer: resumeScheduledTransferReducer,
  cancelScheduledTransfer: cancelScheduledTransferReducer,
  updateScheduledTransfer: updateScheduledTransferReducer,
  getSingleTransfers: getSingleTransfersReducer,
  getAirports: getAirportsReducer,
  getFlightTransactions: getFlightTransactionsReducer,
  getAggregatorQRCode: getAggregatorQRCodeReducer,
  searchFlights: searchFlightsReducer,
  getSingleFlight: getSingleFlightReducer,
  bookFlight: bookFlightReducer,
  businessDeletion: businessDeletionReducer,
  getAllCountries: getAllCountriesReducer,
  getCountryById: getCountryByIdReducer,
  getActiveGiftCardProviders: getActiveGiftCardProvidersReducer,
  makeGiftCardPayments: makeGiftCardPaymentsReducer,
  getCustomerTransactions: getCustomersTransactionsReducer,
  getActiveGiftCardProviderById: getActiveGiftCardProviderByIdReducer,
  ninVerification: ninVerificationReducer,
  representativeAddress: representativeAddressReducer,
  getVirtualAccounts: getVirtualAccountsReducer,
  getVirtualAccountBalance: getVirtualAccountBalanceReducer,
  getVirtualAccountDetails: getVirtualAccountDetailsReducer,
  getVirtualAccountTransactions: getVirtualAccountTransactionsReducer,
  createPermanentVAccount: createPermanentVAccountReducer,
  createTransientVAccount: createTransientVAccountReducer,
  saveBeneficiary: saveBeneficiaryReducer,
  getWalletTransactions: getWalletTransactionsReducer,
  developerOptIn: optInReducer,
  environment: environmentReducer,
  getAllGiftCards: getAllGiftCardsReducer,
  getGiftCardPrices: getGiftCardPricesReducer,
  checkSlugExists: checkSlugExistReducer,
  getBusinessById: getBusinessByIdReducer,
  getDirectorsByBusinessId: getDirectorsByBusinessIdReducer,
  resetSecretKey: resetSecretKeyReducer,
  updateWebhook: updateWebhookReducer,
  beneficiaryDelete: deleteBeneficiaryReducer,
  updateRole: updateRoleReducer,
  createBusinessDirector: businessDirectorCreationReducer,
  cddResponses: cddResponsesReducer,
  submitResidentPermit: submitResidentPermitReducer,
  submitRejectedResidentPermit: submitRejectedResidentPermitReducer,
  getDirectorAdditionalInfo: getDirectorAdditionalInfoReducer,
  submitDirectorAdditionalInfo: submitDirectorAdditionalInfoReducer,
  bulkApproveTransaction: bulkApproveTransactionReducer,
  bulkDeclineTransaction: bulkDeclineTransactionReducer,
  saveBillBeneficiary: saveBillBeneficiaryReducer,
  deleteBillBeneficiary: deleteBillBeneficiaryReducer,
  updateBillBeneficiary: updateBillBeneficiaryReducer,
  createOutletTransactionMandate: createOutletTransactionMandateReducer,
  getOutletTransactionMandates: getOutletTransactionMandateReducer,
  updateOutletTransactionMandate: updateOutletTransactionMandateReducer,
  getTransactionsPendingApproval: getPendingTransactionsReducer,
  getAllowedBusinessTypes: allowedBusinessTypesFetchReducer,
  getCircuitProducts: circuitProductsFetchReducer,
  getCircuitUserHistory: circuitUserHistoryReducer,
  circuitMakePayment: circuitMakePaymentReducer,
  getSavingsBuckets: getSavingsBucketReducer,
  getSavingsBucketsByAccountNumber: getBucketsByAccountNumberReducer,
  createSavingsBucket: createSavingsBucketReducer,
  rolloverSavingsBucket: rolloverBucketReducer,
  updateSavingsBucket: updateBucketNameReducer,
  deleteSavingsBucket: deleteBucketReducer,
  getSavingsBucketTransactions: getBucketTransactionsReducer,
  bucketTopUpDeposit: bucketTopUpDepositReducer,
  searchSavingsDeposits: searchDepositsReducer,
  makeSavingsWithdrawal: makeWithdrawalReducer,
  searchSavingsWithdrawals: searchWithdrawalsReducer,
  createSavingsAutoDebitConfig: createAutoDebitConfigReducer,
  searchSavingsAutoDebitConfigs: searchAutoDebitConfigsReducer,
  deleteSavingsAutoDebitConfig: deleteAutoDebitConfigReducer,
  editSavingsAutoDebitConfig: editAutoDebitConfigReducer,
  createSavingsSpendAndStash: createSpendAndStashReducer,
  searchSavingsSpendAndStash: searchSpendAndStashReducer,
  deleteSavingsSpendAndStash: deleteSpendAndStashReducer,
  editSavingsSpendAndStash: editSpendAndStashReducer,
  savingsTotalAccruedInterest: totalAccruedInterestReducer,
  savingsTotalAccruedInterestByUser: totalAccruedInterestByUserReducer,
  savingsDailyAccruedInterest: dailyAccruedInterestReducer,
  savingsRates: savingsRatesReducer,
  getSavingsAccountLogos: getAccountLogosReducer,
  createSavingsAccountLogo: createAccountLogoReducer,
  searchSavingsProducts: searchSavingsProductsReducer,
};

const rootReducer = combineReducers(reducers);

const appReducer = (state: RootState | undefined, action: AnyAction) => {
  if (action.type === 'RESET_STORE') {
    return combineReducers(reducers)(undefined, action);
  }

  return rootReducer(state, action);
};

export const store = configureStore({
  reducer: appReducer,
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
